(() => {
  if (customElements.get('addax-cart-suggestions')) return;
  let adding = false;
  const memory = { choices: new Map(), order: [], index: 0, collapsed: null };
  class AddaxCartSuggestions extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.acs__track');
      if (!this.track || this.controller) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.validation = this.querySelector('.acs__validation');
      this.shortScreen = matchMedia('(max-height: 600px)');
      this.setCollapsed(memory.collapsed ?? this.shortScreen.matches);
      this.shortScreen.addEventListener('change', () => this.setCollapsed(memory.collapsed ?? this.shortScreen.matches), options);
      this.restoring = true;
      this.querySelectorAll('.acs__card').forEach(card => {
        const select = card.querySelector('select');
        const value = memory.choices.get(card.dataset.productId);
        if (select && value && [...select.options].some(option => option.value === value)) {
          select.value = value;
          this.updateVariant(select);
        }
      });
      requestAnimationFrame(() => {
        if (!this.isConnected) return;
        const candidates = [...memory.order.slice(memory.index), ...memory.order.slice(0, memory.index).reverse()];
        const cards = [...this.track.children];
        const anchor = candidates.map(id => cards.find(card => card.dataset.productId === id)).find(Boolean);
        if (anchor) this.track.scrollLeft = anchor.offsetLeft - cards[0].offsetLeft;
        this.restoring = false;
        this.remember();
        this.schedule();
      });
      document.addEventListener('pointerdown', () => this.hideValidation(), options);
      this.addEventListener('keydown', event => {
        if (event.key === 'Escape' && this.validation && !this.validation.hidden) {
          event.stopPropagation();
          this.suppressEscapeUp = true;
          this.hideValidation();
        }
      }, { ...options, capture: true });
      this.addEventListener('keyup', event => {
        if (event.key === 'Escape' && this.suppressEscapeUp) {
          event.stopPropagation();
          this.suppressEscapeUp = false;
        }
      }, { ...options, capture: true });
      // Keep native validation as a fallback until this component is ready.
      this.querySelectorAll('.acs__form').forEach(form => { form.noValidate = true; });
      this.addEventListener('change', event => {
        // Dawn cart-items treats every bubbling change as a quantity update.
        event.stopPropagation();
        const select = event.target.closest('select[name="id"]');
        if (!select?.value) return;
        this.hideValidation();
        select.removeAttribute('aria-invalid');
        this.updateVariant(select);
        this.remember();
      }, options);
      this.addEventListener('submit', event => this.add(event), options);
      this.addEventListener('click', event => {
        if (event.target.closest('[data-acs-toggle]')) {
          memory.collapsed = !this.classList.contains('is-collapsed');
          this.setCollapsed(memory.collapsed);
          return;
        }
        const arrow = event.target.closest('[data-acs-direction]');
        if (!arrow) return;
        const card = this.track.firstElementChild;
        if (!card) return;
        const step = card.getBoundingClientRect().width + parseFloat(getComputedStyle(this.track).gap || 0);
        this.track.scrollBy({ left: Number(arrow.dataset.acsDirection) * step, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
      }, options);
      this.track.addEventListener('scroll', () => { this.hideValidation(); this.remember(); this.schedule(); }, { ...options, passive: true });
      this.resize = new ResizeObserver(() => { this.hideValidation(); this.schedule(); });
      this.resize.observe(this.track);
      this.schedule();
    }
    setCollapsed(value) {
      this.classList.toggle('is-collapsed', value);
      this.querySelector('[data-acs-toggle]').setAttribute('aria-expanded', String(!value));
      this.querySelector('.acs__toggle-mark').textContent = value ? '+' : '−';
      this.hideValidation();
      this.schedule();
      if (!value && this.shortScreen?.matches && memory.collapsed === false) {
        requestAnimationFrame(() => {
          const body = this.closest('.addax-cart-body');
          if (body) body.scrollTop += this.getBoundingClientRect().top - body.getBoundingClientRect().top;
        });
      }
    }
    updateVariant(select) {
      const option = select.selectedOptions[0];
      const card = select.closest('.acs__card');
      card.querySelector('[data-acs-price]').textContent = option.dataset.price;
      const compare = card.querySelector('[data-acs-compare]');
      compare.textContent = option.dataset.compare || '';
      compare.hidden = !option.dataset.compare;
      compare.parentElement.classList.toggle('is-sale', !!option.dataset.compare);
      const image = card.querySelector('img');
      if (image && option.dataset.image) image.src = option.dataset.image;
      card.querySelectorAll('[data-acs-link]').forEach(link => {
        const url = new URL(link.href, location.origin);
        url.searchParams.set('variant', select.value);
        link.href = url.pathname + url.search + url.hash;
      });
      card.querySelector('.acs__error').hidden = true;
    }
    remember() {
      if (this.restoring || !this.isConnected) return;
      const cards = [...this.track.children];
      cards.forEach(card => {
        const select = card.querySelector('select');
        if (select) memory.choices.set(card.dataset.productId, select.value);
      });
      if (this.classList.contains('is-collapsed')) return;
      memory.order = cards.map(card => card.dataset.productId);
      const step = cards[0]?.getBoundingClientRect().width + parseFloat(getComputedStyle(this.track).gap || 0);
      memory.index = step ? Math.round(this.track.scrollLeft / step) : 0;
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.resize?.disconnect();
      cancelAnimationFrame(this.frame);
    }
    schedule() {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => {
        const max = this.track.scrollWidth - this.track.clientWidth;
        this.querySelectorAll('[data-acs-direction]').forEach(button => {
          button.disabled = Number(button.dataset.acsDirection) < 0 ? this.track.scrollLeft <= 2 : this.track.scrollLeft >= max - 2;
        });
      });
    }
    hideValidation() {
      if (this.validation) this.validation.hidden = true;
    }
    showValidation(field, message = 'Please choose an option.') {
      const bubble = this.validation;
      if (!bubble || !field) return;
      bubble.textContent = message;
      bubble.hidden = false;
      const area = this.getBoundingClientRect();
      const anchor = field.getBoundingClientRect();
      const width = bubble.getBoundingClientRect().width;
      const left = Math.max(0, Math.min(anchor.left - area.left, area.width - width));
      bubble.style.left = left + 'px';
      bubble.style.top = (anchor.top - area.top - bubble.offsetHeight - 9) + 'px';
      bubble.style.setProperty('--acs-tip-left', Math.max(12, Math.min(anchor.left - area.left - left + 18, width - 12)) + 'px');
    }
    async add(event) {
      const form = event.target.closest('.acs__form');
      if (!form) return;
      event.preventDefault();
      event.stopPropagation();
      if (adding) return;
      const variant = form.querySelector('[name="id"]');
      const error = form.querySelector('.acs__error');
      if (!variant?.value) {
        error.hidden = true;
        variant?.setAttribute('aria-invalid', 'true');
        variant?.focus({ preventScroll: true });
        this.showValidation(variant);
        return;
      }
      const drawer = this.closest('cart-drawer');
      if (!window.AddaxCart || !drawer) { HTMLFormElement.prototype.submit.call(form); return; }
      this.hideValidation();
      error.hidden = true;
      this.remember();
      const data = new FormData(form);
      const name = form.dataset.productTitle;
      const button = form.querySelector('.acs__add');
      adding = true;
      button.classList.add('is-loading');
      try {
        await window.AddaxCart.add(data, { source: 'addax-cart-suggestions', openDrawer: true });
        const live = drawer.querySelector('#CartDrawer-LiveRegionText');
        if (live) { live.removeAttribute('aria-hidden'); live.textContent = name + ' added to your bag.'; }
        const focus = drawer.querySelector('.acs__heading') || drawer.querySelector('.drawer__close');
        if (drawer.classList.contains('active') && typeof trapFocus === 'function') trapFocus(drawer.querySelector('#CartDrawer'), focus);
        const status = drawer.querySelector('.acs__status');
        if (status) { status.textContent = '✓ ' + name + ' added to your bag'; status.hidden = false; setTimeout(() => { status.hidden = true; }, 2300); }
      } catch (failure) {
        window.AddaxCart.report(failure.message, window.AddaxCart.busy);
      } finally {
        adding = false;
        button.classList.remove('is-loading');
      }
    }
  }
  customElements.define('addax-cart-suggestions', AddaxCartSuggestions);
})();

/* Compact presentation of the existing Growave trust badge. No hard-coded rating/count. */
(() => {
  if (customElements.get('addax-cart-reviews')) return;
  class AddaxCartReviews extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.attempts = 0;
      this.attach();
      const drawer = this.closest('cart-drawer');
      if (drawer) {
        this.drawerObserver = new MutationObserver(() => {
          if (!this.root && drawer.classList.contains('active')) {
            clearTimeout(this.timer); this.attempts = 0; this.attach();
          }
        });
        this.drawerObserver.observe(drawer, { attributes: true, attributeFilter: ['class'] });
      }
    }
    attach() {
      if (!this.isConnected) return;
      const host = this.querySelector('.gw-rv-trust-badge-widget-placeholder');
      if (!host) return;
      const root = host.shadowRoot;
      if (!root) {
        if (this.attempts++ < 60) this.timer = setTimeout(() => this.attach(), 500);
        return;
      }
      this.root = root;
      this.observer = new MutationObserver(() => this.sync());
      this.observer.observe(root, { childList: true, characterData: true, subtree: true, attributes: true, attributeFilter: ['aria-label','style'] });
      this.sync();
    }
    sync() {
      const badge = this.root.querySelector('.gw-rv-trust-badge');
      const count = badge?.querySelector('.gw-text--heading-3xl')?.textContent.trim();
      const sourceStars = badge?.querySelector('.gw-rv-rating-stars');
      if (!count || !/^\d[\d,.\s]*$/.test(count)) return;
      const label = sourceStars?.getAttribute('aria-label') || '';
      const colour = sourceStars ? getComputedStyle(sourceStars).getPropertyValue('--gw-rv-icon-starColor').trim() : '';
      const signature = count + '|' + label + '|' + colour + '|' + (sourceStars?.innerHTML || '');
      if (signature === this.signature) return;
      this.signature = signature;
      this.querySelector('[data-acr-label]').textContent = count + ' VERIFIED USER REVIEWS';
      this.querySelector('a').setAttribute('aria-label', 'Read ' + count + ' verified user reviews' + (label ? '. ' + label : ''));
      const stars = this.querySelector('[data-acr-stars]');
      const svgs = sourceStars ? [...sourceStars.querySelectorAll('svg')] : [];
      stars.replaceChildren(...svgs.map(svg => svg.cloneNode(true)));
      stars.hidden = !svgs.length;
      stars.setAttribute('aria-hidden', 'true');
      if (colour) this.style.setProperty('--acr-star-color', colour);
    }
    disconnectedCallback() {
      clearTimeout(this.timer);
      this.observer?.disconnect();
      this.drawerObserver?.disconnect();
      this.controller?.abort();
      this.controller = null;
      this.root = null;
      this.signature = null;
    }
  }
  customElements.define('addax-cart-reviews', AddaxCartReviews);
})();

/* Show honest loading feedback while Growave mounts the reviews page. */
(() => {
  function startReviewsLoading() {
    document.querySelectorAll('.gw-rv-reviews-page-widget-placeholder').forEach(host => {
      if (host.dataset.addaxLoading) return;
      host.dataset.addaxLoading = 'true';
      const notice = document.createElement('p');
      notice.className = 'addax-reviews-loading';
      notice.setAttribute('role', 'status');
      notice.textContent = 'Loading customer reviews…';
      host.before(notice);
      let attempts = 0;
      const check = () => {
        if (!host.isConnected) { clearInterval(timer); notice.remove(); return; }
        const root = host.shadowRoot || host;
        // Wait for actual review content, not merely an empty application root.
        if (root.querySelector('[role="tablist"], [role="tabpanel"]')) {
          clearInterval(timer); notice.remove(); return;
        }
        if (++attempts === 40) {
          notice.classList.add('is-delayed');
          notice.textContent = 'Reviews are taking a little longer to load. You can refresh this page to try again.';
        }
      };
      const timer = setInterval(check, 500);
      check();
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', startReviewsLoading, { once: true });
  else startReviewsLoading();
  document.addEventListener('shopify:section:load', startReviewsLoading);
})();
